import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import MDButton from "components/MDButton";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import { useMaterialUIController } from "context";

const BackupDialog = ({ open, setOpen, handleSubmitBackup, loading }) => {
  const [controller] = useMaterialUIController();
  const { activePage, darkMode } = controller;
  const [selectedOption, setSelectedOption] = useState("");

  const handleOptionChange = useCallback((e) => {
    setSelectedOption(e.target.value);
  }, []);

  useEffect(() => {
    setSelectedOption("");
  }, [open]);

  return (
    <Dialog
      sx={(theme) => CTLDialogContent(theme, { darkMode })}
      open={open}
      onClose={() => !loading && setOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontWeight: "bold",
          fontSize: "1.2rem",
        }}
      >
        <IconButton size="small" disabled>
          <BackupIcon fontSize="medium" color="success" />
        </IconButton>
        System Backup
      </DialogTitle>

      <DialogContent
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Typography variant="body1" sx={{ mb: 1 }}>
          Please select the type of backup you want to perform:
        </Typography>

        <RadioGroup value={selectedOption} onChange={handleOptionChange}>
          <FormControlLabel
            value="config"
            control={<Radio sx={{ alignSelf: "flex-start", mt: 0.6 }} />}
            label={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Backup Configuration Only
                </Typography>
                <Typography variant="caption" color="text">
                  Creates a backup of all system configurations, excluding data.
                </Typography>
              </Box>
            }
            sx={{
              alignItems: "flex-start",
              marginBottom: 1,
            }}
          />

          <FormControlLabel
            value="full"
            control={<Radio sx={{ alignSelf: "flex-start", mt: 0.6 }} />}
            label={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Backup Configuration and Data
                </Typography>
                <Typography variant="caption" color="text">
                  Includes configuration and complete database backup.
                </Typography>
              </Box>
            }
            sx={{
              alignItems: "flex-start",
            }}
          />
        </RadioGroup>
      </DialogContent>

      <DialogActions
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
        style={{
          justifyContent: "flex-end",
          padding: "16px 24px",
        }}
      >
        <MDButton
          variant="contained"
          color="secondary"
          onClick={() => setOpen(false)}
          disabled={loading}
        >
          Cancel
        </MDButton>

        <MDButton
          variant="contained"
          color="success"
          onClick={() => handleSubmitBackup(selectedOption)}
          disabled={!selectedOption || loading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          {loading ? "Processing..." : "Start Backup"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default BackupDialog;
